import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import GoogleButton from "@/components/GoogleButton";
import { useSEO } from "@/lib/useSEO";

export default function Login() {
  const { user, login, error } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const queryError = params.get("error");

  useSEO({
    title: "Sign in — Hearth & Crate",
    description: "Sign in to your Hearth & Crate workspace.",
    canonical: "https://hearthpro.co.uk/login",
  });

  useEffect(() => {
    if (user && user.email) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(email, password);
    setBusy(false);
    if (ok) navigate("/dashboard", { replace: true });
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f7f5f0]">
      {/* Left – Form */}
      <div className="flex flex-col px-8 sm:px-16 py-10 lg:py-20">
        <div className="font-display text-xl font-medium tracking-tight">
          Hearth<span className="text-[#c0612f]">.</span>
        </div>

        <div className="flex-1 flex items-center">
          <div className="w-full max-w-sm animate-fade-up">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#8a8476] mb-4">
              Sign in
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-none">
              Run your<br />
              <span className="text-[#4e7b5b]">food business.</span>
            </h1>
            <p className="mt-4 text-sm text-[#57534e] leading-relaxed">
              Compliance logs, audits, events, invoices, batches and training — one place, audit-ready.
            </p>

            <GoogleButton className="mt-8" />

            {params.get("verified") === "1" && (
              <div data-testid="verify-banner-success" className="mt-6 flex items-start gap-2 bg-[#e8f0e8] border border-[#c5dcc5] text-[#1b3b36] rounded-[3px] px-4 py-3 text-sm">
                <span className="font-medium">Email verified.</span>
                <span>You&apos;re good to sign in.</span>
              </div>
            )}
            {params.get("verified") === "0" && (
              <div data-testid="verify-banner-error" className="mt-6 flex items-start gap-2 bg-[#fbeae8] border border-[#f1c5be] text-[#a23a30] rounded-[3px] px-4 py-3 text-sm">
                <span className="font-medium">Verification link is invalid or expired.</span>
                <span>Sign in to request a fresh one.</span>
              </div>
            )}

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-[#e5e2d9]" />
              <span className="text-[10px] uppercase tracking-[0.28em] text-[#8a8476]">or with email</span>
              <div className="flex-1 h-px bg-[#e5e2d9]" />
            </div>

            <form className="space-y-5" onSubmit={onSubmit} data-testid="login-form">
              <div>
                <Label htmlFor="email" className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-2 h-11 bg-white border-[#e5e2d9] focus-visible:ring-[#1b3b36]/20 focus-visible:border-[#1b3b36] rounded-[4px]"
                />
              </div>
              <div>
                <Label htmlFor="password" className="text-[11px] uppercase tracking-[0.22em] text-[#8a8476]">
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  data-testid="login-password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-11 bg-white border-[#e5e2d9] focus-visible:ring-[#1b3b36]/20 focus-visible:border-[#1b3b36] rounded-[4px]"
                />
              </div>

              {(error || queryError) && (
                <div data-testid="login-error" className="text-sm text-[#d94f3d] bg-[#fbeae8] px-3 py-2 rounded-[3px]">
                  {error || queryError}
                </div>
              )}

              <Button
                type="submit"
                data-testid="login-submit-button"
                disabled={busy}
                className="w-full h-11 bg-[#1b3b36] hover:bg-[#2c5a53] text-white rounded-[4px] font-medium tracking-wide"
              >
                {busy ? "Signing in…" : "Sign in"}
              </Button>

              <div className="text-sm text-[#57534e]">
                Don&apos;t have an account?{" "}
                <Link to="/signup" data-testid="login-to-signup" className="text-[#1b3b36] underline underline-offset-2">
                  Create one
                </Link>
                <span className="mx-2 text-[#c8c2b6]">·</span>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-[#1b3b36] underline underline-offset-2">
                  Forgot password?
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className="text-xs text-[#8a8476] flex items-center gap-4">
          <span>© {new Date().getFullYear()} Hearth Operations</span>
          <Link to="/legal/terms" className="hover:text-[#1b3b36]" data-testid="login-link-terms">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-[#1b3b36]" data-testid="login-link-privacy">Privacy</Link>
          <Link to="/legal/cookies" className="hover:text-[#1b3b36]" data-testid="login-link-cookies">Cookies</Link>
        </div>
      </div>

      {/* Right – Image */}
      <div className="relative hidden lg:block overflow-hidden grain">
        <img
          src="https://images.pexels.com/photos/4253133/pexels-photo-4253133.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt="Chef"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1b3b36]/70 via-[#1b3b36]/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="text-[11px] uppercase tracking-[0.32em] text-white/70 mb-2">
            Sites • compliant
          </div>
          <div className="font-display text-3xl font-medium tracking-tight max-w-md leading-tight">
            &ldquo;Every batch logged. Every fridge checked. Every audit ready — kitchens, cafés, and shops.&rdquo;
          </div>
          <div className="mt-6 grid grid-cols-3 max-w-md gap-3 text-xs">
            {[
              ["Compliance", "100%"],
              ["Invoices", "On time"],
              ["Audits", "Ready"],
            ].map(([k, v]) => (
              <div key={k} className="border-l border-white/30 pl-3">
                <div className="opacity-70 uppercase tracking-[0.22em] text-[10px]">{k}</div>
                <div className="text-base font-display mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
